import type { Editor, EditorEvents } from '@tiptap/core'
import { debounce } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTextEditor } from '../context'
import { editorBlurHandler } from '../utils'

type UseEditorVisibilityParams = {
  /**
   * When true, the hook will always return this value. Event handler setup
   * is skipped.
   *
   * Defaults to false.
   */
  alwaysVisible?: boolean
  /**
   * Debounce delay for the blur event.
   *
   * Defaults to 0.
   */
  blurDebounceDelay?: number
  /**
   * Additional conditions for visibility. `editor.isEditable` is always
   * checked, and it will fall back to `editor.view.hasFocus()` if `visibleOn`
   * is false or not provided.
   */
  visibleOn?: boolean
}

/**
 * Uses the editor's focus and blur events to evaluate if a component
 * of the editor should be visible. The visibility logic can be customized.
 */
export const useEditorVisibility = ({
  alwaysVisible = false,
  blurDebounceDelay = 0,
  visibleOn: visibleOnProp = false,
}: UseEditorVisibilityParams = {}): boolean => {
  const { editor } = useTextEditor()
  const visibleOn = useRef<boolean>(visibleOnProp)
  const shouldShow = useCallback(
    (editor: Editor) =>
      editor.isEditable && (visibleOn.current || editor.view.hasFocus()),
    []
  )

  const [isVisible, setVisibility] = useState<boolean>(shouldShow(editor))

  useEffect(() => {
    visibleOn.current = visibleOnProp
  }, [visibleOnProp])

  useEffect(() => {
    if (alwaysVisible) {
      return
    }

    const focusHandler = ({ editor }: EditorEvents['focus']) =>
      setVisibility(shouldShow(editor))
    const blurHandler = debounce(
      editorBlurHandler(({ editor }) => setVisibility(shouldShow(editor))),
      blurDebounceDelay
    )

    editor.on('focus', focusHandler)
    editor.on('blur', blurHandler)

    return () => {
      blurHandler.cancel()
      if (!editor.isDestroyed) {
        editor.off('focus', focusHandler)
        editor.off('blur', blurHandler)
      }
    }
  }, [editor, alwaysVisible, blurDebounceDelay, shouldShow])

  return alwaysVisible || isVisible
}
